import React, { useState, useRef, useEffect } from 'react';
import { Camera, Smile, Paperclip, Send, CornerUpLeft, X, Mic, Lock, ChevronUp, Trash2, Square } from 'lucide-react';
import { uploadFile } from '../services/api';
import { subscribe as subscribePhone } from '../services/softphone';
import {
  isVoiceSupported, startRecording, stopRecording, cancelRecording, buzz, fmtSecs
} from '../services/voiceRecorder';
import CameraModal from './CameraModal';
import EmojiPicker from './EmojiPicker';
import '../styles/MessageInput.css';

// Пороги жестов (как в WhatsApp): вверх — замок, влево — отмена
const LOCK_THRESHOLD = 50;
const CANCEL_THRESHOLD = 80;
const MIN_VOICE_SECS = 1;
const MAX_VOICE_SECS = 300;
const LIVE_BARS = 24;

function MessageInput({ channelId, onSendMessage, onStartTyping, onStopTyping, replyToMessage, onCancelReply }) {
  const [message, setMessage] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);
  const fileInputRef = useRef(null);
  const textareaRef = useRef(null);
  const typingTimeoutRef = useRef(null);

  // --- Голосовые сообщения ---
  // idle → recording (палец держит) → locked (защёлкнут замок) → preview (запись готова)
  const [voiceState, setVoiceState] = useState('idle');
  const [voiceSecs, setVoiceSecs] = useState(0);
  const [liveBars, setLiveBars] = useState([]);
  const [drag, setDrag] = useState({ x: 0, y: 0 });
  const [voiceHint, setVoiceHint] = useState('');
  const [voiceSending, setVoiceSending] = useState(false);
  const [recorded, setRecorded] = useState(null);       // { blob, secs, peaks, mime }
  const [previewUrl, setPreviewUrl] = useState(null);
  const [phoneBusy, setPhoneBusy] = useState(false);
  const dragRef = useRef({ startX: 0, startY: 0, locked: false, cancelled: false, active: false });
  const voiceStateRef = useRef('idle');
  const voiceSupported = isVoiceSupported();

  useEffect(() => { voiceStateRef.current = voiceState; }, [voiceState]);

  // Микрофон один: во время звонка из чата запись недоступна
  useEffect(() => subscribePhone((p) => {
    setPhoneBusy(p.status !== 'idle' && p.status !== 'ended' && p.status !== 'error');
  }), []);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Calculate new height (max 4 lines, ~24px per line)
    const maxHeight = 96; // ~4 lines
    const newHeight = Math.min(textarea.scrollHeight, maxHeight);

    textarea.style.height = newHeight + 'px';
  }, [message]);

  const selectedFilesRef = useRef(selectedFiles);
  useEffect(() => {
    selectedFilesRef.current = selectedFiles;
  }, [selectedFiles]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      selectedFilesRef.current.forEach(file => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, []);

  const handleEmojiSelect = (emoji) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const text = message;
    const before = text.substring(0, start);
    const after = text.substring(end);

    const newText = before + emoji + after;
    setMessage(newText);

    // Восстанавливаем позицию курсора после эмодзи
    setTimeout(() => {
      textarea.focus();
      const newPos = start + emoji.length;
      textarea.setSelectionRange(newPos, newPos);
    }, 0);
  };

    const handleMessageChange = (e) => {
    setMessage(e.target.value);

    if (e.target.value.length > 0) {
      onStartTyping();

      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }

      typingTimeoutRef.current = setTimeout(() => {
        onStopTyping();
      }, 3000);
    } else {
      onStopTyping();
    }
  };

  const handleKeyDown = (e) => {
    // Enter без Shift - отправка сообщения
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e);
    }
    // Shift+Enter - новая строка (работает по умолчанию)
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (selectedFiles.length > 0) {
      handleFilesUpload();
    } else if (message.trim()) {
      onSendMessage(message.trim(), 'text', replyToMessage?.id || null);
      setMessage('');
      onStopTyping();
      if (onCancelReply) onCancelReply();
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);

    if (files.length === 0) return;

    const maxSize = 10 * 1024 * 1024 * 1024;
    const oversizedFiles = files.filter(f => f.size > maxSize);

    if (oversizedFiles.length > 0) {
      alert('Некоторые файлы слишком большие. Максимальный размер: 10GB');
      return;
    }

    const filesToAdd = files;

    const filesWithPreview = filesToAdd.map(file => ({
      file,
      preview: (file.type.startsWith('image/') || file.type.startsWith('video/'))
        ? URL.createObjectURL(file)
        : null,
      name: file.name,
      size: file.size,
      type: file.type
    }));

    setSelectedFiles(prev => [...prev, ...filesWithPreview]);

    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleCameraCapture = (photos) => {
    const filesWithPreview = photos.map(({ file, preview }) => ({
      file,
      preview,
      name: file.name,
      size: file.size,
      type: file.type
    }));

    setSelectedFiles(prev => [...prev, ...filesWithPreview]);
  };

  const removeFile = (index) => {
    setSelectedFiles(prev => {
      const file = prev[index];
      if (file.preview) {
        URL.revokeObjectURL(file.preview);
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleFilesUpload = async () => {
    if (selectedFiles.length === 0) return;

    setIsUploading(true);
    setUploadProgress(0);

    const BATCH_SIZE = 10;
    const batches = [];
    for (let i = 0; i < selectedFiles.length; i += BATCH_SIZE) {
      batches.push(selectedFiles.slice(i, i + BATCH_SIZE));
    }

    try {
      for (let batchIndex = 0; batchIndex < batches.length; batchIndex++) {
        const batch = batches[batchIndex];

        setUploadProgress(Math.round((batchIndex / batches.length) * 100));

        const formData = new FormData();
        formData.append('channelId', channelId);
        // Подпись только к первой партии
        if (batchIndex === 0 && message.trim()) {
          formData.append('content', message.trim());
        }
        if (batchIndex === 0 && replyToMessage?.id) {
          formData.append('replyToMessageId', replyToMessage.id);
        }
        batch.forEach(({ file }) => {
          formData.append('files', file);
        });

        await uploadFile(channelId, formData, '', (evt) => {
          if (evt && evt.total) {
            const batchFrac = evt.loaded / evt.total;
            const overall = ((batchIndex + batchFrac) / batches.length) * 100;
            setUploadProgress(Math.min(99, Math.round(overall)));
          }
        });
      }

      setUploadProgress(100);

      selectedFiles.forEach(file => {
        if (file.preview) URL.revokeObjectURL(file.preview);
      });

      setMessage('');
      setSelectedFiles([]);
      if (onCancelReply) onCancelReply();
    } catch (error) {
      console.error('File upload failed:', error);
      const isVideo = selectedFiles.some(f => (f.type || '').startsWith('video/'));
      alert(
        (isVideo
          ? 'Не удалось загрузить видео. '
          : 'Не удалось загрузить файлы. ') +
        'Проверьте интернет и попробуйте ещё раз. Большие видео лучше отправлять при стабильном Wi-Fi.'
      );
    } finally {
      setTimeout(() => {
        setIsUploading(false);
        setUploadProgress(0);
      }, 500);
    }
  };

  // ================= Голосовые сообщения =================

  const showHint = (text) => {
    setVoiceHint(text);
    setTimeout(() => setVoiceHint(''), 2200);
  };

  const resetVoice = () => {
    setVoiceState('idle');
    setVoiceSecs(0);
    setLiveBars([]);
    setDrag({ x: 0, y: 0 });
    setRecorded(null);
    dragRef.current = { startX: 0, startY: 0, locked: false, cancelled: false, active: false };
  };

  useEffect(() => {
    if (!recorded) {
      setPreviewUrl(null);
      return;
    }
    const url = URL.createObjectURL(recorded.blob);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [recorded]);

  const sendVoice = async (rec) => {
    if (!rec || !rec.blob || rec.blob.size === 0) { resetVoice(); return; }

    const base = (rec.mime || '').split(';')[0];
    const ext = base.includes('mp4') || base.includes('aac') ? 'm4a'
      : base.includes('ogg') ? 'ogg'
      : base.includes('mpeg') ? 'mp3' : 'webm';
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T-]/g, '');
    const file = new File([rec.blob], `voice-${stamp}.${ext}`, { type: base });

    const formData = new FormData();
    formData.append('channelId', channelId);
    formData.append('files', file);
    formData.append('voiceSecs', String(rec.secs));
    formData.append('voicePeaks', JSON.stringify(rec.peaks || []));
    if (replyToMessage?.id) formData.append('replyToMessageId', replyToMessage.id);

    setVoiceSending(true);
    resetVoice();
    try {
      await uploadFile(channelId, formData);
      if (onCancelReply) onCancelReply();
    } catch (err) {
      console.error('Voice upload failed:', err);
      alert('Не удалось отправить голосовое. Проверьте интернет и попробуйте ещё раз.');
    } finally {
      setVoiceSending(false);
    }
  };

  const beginRecording = async (e) => {
    if (phoneBusy) { showHint('Идёт звонок — микрофон занят'); return; }

    dragRef.current = {
      startX: e.clientX, startY: e.clientY,
      locked: false, cancelled: false, active: true
    };
    setDrag({ x: 0, y: 0 });
    setVoiceSecs(0);
    setLiveBars([]);
    setVoiceState('recording');
    buzz(15);

    try {
      await startRecording({
        maxSecs: MAX_VOICE_SECS,
        onSecs: setVoiceSecs,
        onLevel: (level) => setLiveBars(prev => [...prev, level].slice(-LIVE_BARS)),
        onLimit: async () => {
          const rec = await stopRecording();
          buzz(30);
          setRecorded(rec);
          setVoiceState('preview');
          showHint('Дошли до 5 минут — можно отправить или удалить');
        }
      });
    } catch (err) {
      console.error('Mic access failed:', err);
      resetVoice();
      showHint(err && err.name === 'NotAllowedError'
        ? 'Нет доступа к микрофону — разрешите в настройках браузера'
        : 'Микрофон недоступен');
    }
  };

  const handleVoicePointerDown = (e) => {
    if (voiceState !== 'idle' || voiceSending) return;
    e.preventDefault();
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch (err) {}
    beginRecording(e);
  };

  const handleVoicePointerMove = (e) => {
    const d = dragRef.current;
    if (!d.active || voiceStateRef.current !== 'recording') return;

    const dx = Math.min(0, e.clientX - d.startX);
    const dy = Math.min(0, e.clientY - d.startY);
    setDrag({ x: dx, y: dy });

    // Влево — отмена
    if (dx <= -CANCEL_THRESHOLD) {
      d.active = false;
      d.cancelled = true;
      cancelRecording();
      buzz(40);
      resetVoice();
      showHint('Запись отменена');
      return;
    }
    // Вверх — замок: пишем дальше без удержания
    if (dy <= -LOCK_THRESHOLD) {
      d.active = false;
      d.locked = true;
      buzz(30);
      setDrag({ x: 0, y: 0 });
      setVoiceState('locked');
    }
  };

  const handleVoicePointerUp = async () => {
    const d = dragRef.current;
    if (!d.active || voiceStateRef.current !== 'recording') return;
    d.active = false;
    setDrag({ x: 0, y: 0 });

    const rec = await stopRecording();
    if (!rec || rec.secs < MIN_VOICE_SECS) {
      resetVoice();
      showHint('Удерживайте кнопку для записи');
      return;
    }
    sendVoice(rec);
  };

  // Останавливаем запись в готовую (не теряем её), если чат ушёл в фон:
  // Android глушит микрофон при сворачивании и блокировке экрана
  useEffect(() => {
    const onHidden = async () => {
      if (document.visibilityState !== 'hidden') return;
      const st = voiceStateRef.current;
      if (st !== 'recording' && st !== 'locked') return;
      dragRef.current.active = false;
      const rec = await stopRecording();
      if (rec && rec.secs >= MIN_VOICE_SECS) {
        setRecorded(rec);
        setVoiceState('preview');
      } else {
        cancelRecording();
        resetVoice();
      }
    };
    document.addEventListener('visibilitychange', onHidden);
    return () => document.removeEventListener('visibilitychange', onHidden);
  }, []);

  const handleStopToPreview = async () => {
    const rec = await stopRecording();
    if (!rec || rec.secs < MIN_VOICE_SECS) { resetVoice(); return; }
    setRecorded(rec);
    setVoiceState('preview');
  };

  const handleVoiceCancel = () => {
    cancelRecording();
    buzz(20);
    resetVoice();
  };

  const handleVoiceSendClick = async () => {
    if (voiceState === 'locked') {
      const rec = await stopRecording();
      if (!rec || rec.secs < MIN_VOICE_SECS) { resetVoice(); return; }
      sendVoice(rec);
    } else if (voiceState === 'preview') {
      sendVoice(recorded);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };

  return (
    <div className="message-input">
      {replyToMessage && (
        <div className="reply-preview">
          <div className="reply-preview-content">
            <div className="reply-preview-header">
              <CornerUpLeft size={14} strokeWidth={2} className="reply-icon" />
              <span className="reply-to">Ответ для <strong>{replyToMessage.displayName}</strong></span>
            </div>
            <div className="reply-preview-text">
              {replyToMessage.content || (replyToMessage.files?.length > 0 ? `${replyToMessage.files.length} файл(ов)` : 'Сообщение')}
            </div>
          </div>
          <button className="reply-preview-close" onClick={onCancelReply}><X size={14} strokeWidth={2} /></button>
        </div>
      )}

      {selectedFiles.length > 0 && (
        <div className="files-preview">
          <div className="files-preview-header">
            <span className="file-count-badge">
              {selectedFiles.length} {selectedFiles.length > 10 ? `(${Math.ceil(selectedFiles.length / 10)} партии)` : '/ 10'}
            </span>
          </div>
          <div className="files-preview-items">
          {selectedFiles.map((file, index) => (
            <div key={index} className="file-preview-item">
              {file.type.startsWith('image/') ? (
                <img src={file.preview} alt={file.name} className="preview-image" />
              ) : file.type.startsWith('video/') ? (
                <video src={file.preview} className="preview-video" controls />
              ) : (
                <div className="preview-file-icon">📄</div>
              )}
              <div className="file-info">
                <div className="file-name">{file.name}</div>
                <div className="file-size">{formatFileSize(file.size)}</div>
              </div>
              <button
                type="button"
                className="btn-remove-file"
                onClick={() => removeFile(index)}
                title="Удалить"
              >
                <X size={12} strokeWidth={2.5} />
              </button>
            </div>
          ))}
          </div>
        </div>
      )}

      {isUploading && (
        <div className="upload-progress">
          <div className="progress-bar">
            <div
              className="progress-fill"
              style={{ width: uploadProgress + '%' }}
            ></div>
          </div>
          <span>
            {selectedFiles.length > 10
              ? `Загрузка партиями... ${uploadProgress}%`
              : `Загрузка... ${uploadProgress}%`}
          </span>
        </div>
      )}

      {voiceSending && (
        <div className="upload-progress">
          <div className="progress-bar"><div className="progress-fill voice-indeterminate"></div></div>
          <span>Отправляю голосовое…</span>
        </div>
      )}

      {voiceHint && <div className="voice-hint-toast">{voiceHint}</div>}

      <form onSubmit={handleSubmit} className="input-form">
        {voiceState !== 'idle' && (
          <div
            className={`voice-panel ${voiceState === 'preview' ? 'preview' : ''}`}
            style={voiceState === 'recording'
              ? { opacity: Math.max(0.35, 1 - Math.abs(drag.x) / CANCEL_THRESHOLD), transform: `translateX(${drag.x / 3}px)` }
              : undefined}
          >
            {voiceState === 'preview' ? (
              <>
                <button type="button" className="voice-btn-trash" onClick={handleVoiceCancel} title="Удалить запись">
                  <Trash2 size={18} strokeWidth={1.8} />
                </button>
                <audio className="voice-preview-audio" src={previewUrl || undefined} controls preload="metadata" />
                <span className="voice-timer">{fmtSecs(recorded?.secs)}</span>
              </>
            ) : (
              <>
                {voiceState === 'locked' && (
                  <button type="button" className="voice-btn-trash" onClick={handleVoiceCancel} title="Отменить">
                    <Trash2 size={18} strokeWidth={1.8} />
                  </button>
                )}
                <span className="voice-rec-dot" />
                <span className="voice-timer">{fmtSecs(voiceSecs)}</span>
                <div className="voice-live">
                  {Array.from({ length: LIVE_BARS }).map((_, i) => {
                    const level = liveBars[i - (LIVE_BARS - liveBars.length)] || 0;
                    return (
                      <span
                        key={i}
                        className="voice-live-bar"
                        style={{ height: Math.max(3, Math.round(level * 22)) + 'px' }}
                      />
                    );
                  })}
                </div>
                {voiceState === 'recording' ? (
                  <span className="voice-slide-hint">‹ влево — отмена</span>
                ) : (
                  <button type="button" className="voice-btn-stop" onClick={handleStopToPreview} title="Остановить и прослушать">
                    <Square size={14} strokeWidth={2.2} /> Прослушать
                  </button>
                )}
              </>
            )}
          </div>
        )}

        {voiceState === 'idle' && (<>
        <button
          type="button"
          className="btn-camera"
          onClick={() => setIsCameraOpen(true)}
          disabled={isUploading || selectedFiles.length >= 10}
          title="Открыть камеру"
        >
          <Camera size={20} strokeWidth={1.8} />
        </button>

        <button
          type="button"
          className="btn-emoji"
          onClick={() => setIsEmojiPickerOpen(true)}
          disabled={isUploading}
          title="Добавить эмодзи"
        >
          <Smile size={20} strokeWidth={1.8} />
        </button>

        <button
          type="button"
          className="btn-attach"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          title="Прикрепить файлы из галереи"
        >
          <Paperclip size={20} strokeWidth={1.8} />
        </button>

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*,video/*"
          multiple
          onChange={handleFileSelect}
          style={{ display: 'none' }}
        />

        <textarea
          ref={textareaRef}
          className="message-text-input"
          value={message}
          onChange={handleMessageChange}
          onKeyDown={handleKeyDown}
          placeholder={replyToMessage ? "Ответ..." : (selectedFiles.length > 0 ? "Добавьте подпись к файлам..." : "Введите сообщение...")}
          disabled={isUploading}
          rows={1}
        /></>)}

        {/* Умная кнопка: микрофон на пустом поле, самолётик — когда есть что отправить */}
        {voiceSupported && voiceState === 'idle' && !message.trim() && selectedFiles.length === 0 && !isUploading && !voiceSending ? (
          <button
            type="button"
            className={`btn-send btn-voice ${phoneBusy ? 'busy' : ''}`}
            onPointerDown={handleVoicePointerDown}
            onContextMenu={(e) => e.preventDefault()}
            title="Удерживайте для записи голосового"
          >
            <Mic size={20} strokeWidth={1.8} />
          </button>
        ) : voiceState === 'recording' ? (
          <div className="voice-mic-wrap">
            <div className={`voice-lock-hint ${drag.y <= -LOCK_THRESHOLD / 2 ? 'near' : ''}`}>
              <Lock size={14} strokeWidth={2} />
              <ChevronUp size={14} strokeWidth={2} />
            </div>
            <button
              type="button"
              className="btn-send btn-voice recording"
              style={{ transform: `translate(${drag.x / 2}px, ${drag.y / 2}px) scale(1.5)` }}
              onPointerMove={handleVoicePointerMove}
              onPointerUp={handleVoicePointerUp}
              onPointerCancel={handleVoicePointerUp}
              onContextMenu={(e) => e.preventDefault()}
            >
              <Mic size={20} strokeWidth={2} />
            </button>
          </div>
        ) : (voiceState === 'locked' || voiceState === 'preview') ? (
          <button
            type="button"
            className="btn-send btn-voice-send"
            onClick={handleVoiceSendClick}
            title="Отправить голосовое"
          >
            <Send size={20} strokeWidth={1.8} />
          </button>
        ) : (
          <button
            type="submit"
            className="btn-send"
            disabled={(!message.trim() && selectedFiles.length === 0) || isUploading}
          >
            <Send size={20} strokeWidth={1.8} />
          </button>
        )}
      </form>

      <CameraModal
        isOpen={isCameraOpen}
        onClose={() => setIsCameraOpen(false)}
        onCapture={handleCameraCapture}
      />

      {isEmojiPickerOpen && (
        <EmojiPicker
          onSelect={handleEmojiSelect}
          onClose={() => setIsEmojiPickerOpen(false)}
        />
      )}
    </div>
  );
}

export default MessageInput;
